import { createSlice, type PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";

import { notificationsService } from "../services/notifications.service";
import type { FetchNotificationsParams } from "../services/notifications.service";
import type { NotificationItem } from "../types/notifications-api.types";

export type NotificationsConnectionStatus = "idle" | "connecting" | "connected" | "disconnected" | "error";

export interface NotificationsState {
  items: NotificationItem[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
  connectionStatus: NotificationsConnectionStatus;
}

const initialState: NotificationsState = {
  items: [],
  total: 0,
  skip: 0,
  limit: 20,
  hasMore: false,
  isLoading: false,
  error: null,
  connectionStatus: "idle",
};

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchList",
  async (params: FetchNotificationsParams | undefined, { rejectWithValue }) => {
    try {
      return await notificationsService.fetchList(params);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Error al cargar notificaciones";
      return rejectWithValue(message);
    }
  },
);

export const markNotificationAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (params: { id: string; endpoint?: string } | string, { rejectWithValue }) => {
    try {
      if (typeof params === "string") {
        await notificationsService.markAsRead({ id: params });
        return params;
      }
      await notificationsService.markAsRead(params);
      return params.id;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Error al marcar como leída";
      return rejectWithValue(message);
    }
  },
);

export const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setConnectionStatus: (state, action: PayloadAction<NotificationsConnectionStatus>) => {
      state.connectionStatus = action.payload;
    },
    pushNotification: (state, action: PayloadAction<NotificationItem>) => {
      const exists = state.items.some((n) => n.id === action.payload.id);
      if (!exists) {
        state.items = [action.payload, ...state.items];
        state.total = Math.max(state.total, state.items.length);
      }
    },
    setNotificationRead: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const item = state.items.find((n) => n.id === id);
      if (item) {
        item.read_at = new Date().toISOString();
      }
    },
    clearNotificationsError: (state) => {
      state.error = null;
    },
    resetNotificationsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        const isAppend = action.payload.skip > 0;
        if (isAppend) {
          const existingIds = new Set(state.items.map((n) => n.id));
          const newItems = action.payload.data.filter((n) => !existingIds.has(n.id));
          state.items = [...state.items, ...newItems];
        } else {
          state.items = action.payload.data;
        }
        state.total = action.payload.total;
        state.skip = action.payload.skip;
        state.limit = action.payload.limit;
        state.hasMore = action.payload.hasMore;
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) ?? "Error al cargar notificaciones";
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const id = action.payload;
        const item = state.items.find((n) => n.id === id);
        if (item) {
          item.read_at = new Date().toISOString();
        }
      });
  },
});

export const {
  setConnectionStatus,
  pushNotification,
  setNotificationRead,
  clearNotificationsError,
  resetNotificationsState,
} = notificationsSlice.actions;
